<template>
	<FormTitle
		:text="title.zip"
		:required="required.zip"
		:error="errorText.zip1 ? true : errorText.zip2 ? true : false"
		:disabled="disabled"
		:kizuna-type="kizunaType"
	/>

	<div
		class="form_group flex items-center"
		:class="[{ active: hasClass.name, error: errorZipClass }]"
	>
		<div class="relative">
			<input
				type="text"
				v-model="zip1Model"
				:disabled="disabled"
				maxlength="3"
				class="input mr-2 w-[8rem]"
				autocomplete="off"
				tabindex="1"
				placeholder="123"
				@blur="zipCodeCheck('zip1', $event.target)"
			/>
		</div>

		&ensp;-&ensp;
		<div class="relative">
			<input
				type="text"
				v-model="zip2Model"
				:disabled="disabled"
				maxlength="4"
				class="input ml-2 w-[8rem]"
				autocomplete="off"
				tabindex="1"
				placeholder="4567"
				@blur="zipCodeCheck('zip2', $event.target)"
			/>
		</div>
	</div>
	<ErrorText :text="errorText.zip1" />
	<ErrorText :text="errorText.zip2" />

	<FormTitle
		:text="title.address"
		:required="required.address"
		:kizuna-type="kizunaType"
		:error="errorText.address ? true : false"
		class="mt-4"
	/>

	<div
		class="form_group"
		:class="[{ active: hasClass.kana, error: errorAddressClass }]"
	>
		<input
			type="text"
			v-model="addressModel"
			:disabled="disabled"
			:class="formClass"
			maxlength="255"
			class="input"
			autocomplete="off"
			tabindex="1"
			placeholder="東京都新宿区西新宿1-2-3 ⚪︎⚪︎ビル４階"
		/>
	</div>
	<ErrorText :text="errorText.address" />
</template>

<script setup>
import ErrorText from "@/components/atoms/ErrorText.vue";
import FormTitle from "@/components/atoms/FormTitle.vue";
import { onMounted, ref, watch } from "vue";
// API
import { getAddress } from "@/api/common/Address";

/* ---------------------------------------------------------------------------
	props
---------------------------------------------------------------------------- */
const props = defineProps({
	title: Object,
	formClass: [String, Array],
	disabled: Boolean,
	required: Object,
	errorText: Object,
	kizunaType: Number
});

const zip1Model = defineModel("zip1");
const zip2Model = defineModel("zip2");
const addressModel = defineModel("address");
/* ---------------------------------------------------------------------------
data
---------------------------------------------------------------------------- */
const supplementFlag = ref(false);
const hasClass = ref({
	zip: false,
	address: false
});

const errorZipClass = ref("");
const errorAddressClass = ref("");

/* ---------------------------------------------------------------------------
	watch
---------------------------------------------------------------------------- */
watch(
	() => zip1Model.value,
	(newValue, oldValue) => {
		enteredOrNotCheck("zip1", newValue);
	}
);

watch(
	() => zip2Model.value,
	(newValue, oldValue) => {
		enteredOrNotCheck("zip2", newValue);
	}
);

watch(
	() => addressModel.value,
	(newValue, oldValue) => {
		enteredOrNotCheck("address", newValue);
	}
);
/* ---------------------------------------------------------------------------
	入力項目の判定
---------------------------------------------------------------------------- */
function enteredOrNotCheck(flag, value) {
	if (flag == "zip1" || flag == "zip2") {
		if (props.required.zip && (value == "" || value == null)) {
			errorZipClass.value = "input-red";
		} else {
			errorZipClass.value = "";
		}
	} else if (flag == "address") {
		if (props.required.address && (value == "" || value == null)) {
			errorAddressClass.value = "input-red";
		} else {
			errorAddressClass.value = "";
		}
	}
}
/* ---------------------------------------------------------------------------
	郵便番号が入力されたら住所自動入力（ふりがな含む）を行う
---------------------------------------------------------------------------- */
function zipCodeCheck(zip, input) {
	let value = input.value;

	value = value.replace(/[０-９]/g, s =>
		String.fromCharCode(s.charCodeAt(0) - 65248)
	);
	const numericValue = value;
	if (!isNaN(numericValue)) {
		if (zip === "zip1") {
			zip1Model.value = numericValue.toString();
		} else if (zip === "zip2") {
			zip2Model.value = numericValue.toString();
		}
	} else {
		input.value = "";
		if (zip === "zip1") {
			zip1Model.value = "";
		} else if (zip === "zip2") {
			zip2Model.value = "";
		}
	}

	if (zip1Model.value != "" && zip2Model.value != "") {
		updateAddress(zip1Model.value, zip2Model.value);
	}
}

/* ---------------------------------------------------------------------------
	住所自動入力（ふりがな含む）
---------------------------------------------------------------------------- */
async function updateAddress(zip1, zip2) {
	if (zip1 != null && zip2 != null) {
		// 郵便番号が「3桁」「4桁」ではない場合
		if (zip1.length != 3 || zip2.length != 4) {
			return false;
			// 上記以外
		} else {
			// 住所を一旦空にする
			addressModel.valaue = "";
			// ↓ zipCloudはjsonpを使用しないとエラーになる
			const zipCode = zip1 + zip2;

			await getAddress(zipCode)
				.then(res => {
					if (res.data.results != null) {
						const resultData = res.data.results[0];

						// 住所
						const pref = resultData["address1"];
						const add1 = resultData["address2"];
						const add2 = resultData["address3"];
						const address = pref + add1 + add2;

						addressModel.value = address;
					}
				})
				.catch(err => {
					console.log(err);
				});
		}
	}
}
/* ---------------------------------------------------------------------------
	mounted
---------------------------------------------------------------------------- */
onMounted(() => {
	enteredOrNotCheck("zip1", zip1Model.value);
	enteredOrNotCheck("zip2", zip2Model.value);
	enteredOrNotCheck("address", addressModel.value);
});
</script>

<style scoped lang="scss">
@use "/resources/css/_adjust.scss" as *;
.input {
	height: 40px;
	font-size: 1.4rem;
	position: relative;
	font-weight: normal;
	@include trans(0.3);
	padding: 10px 6px 8px;
	border-radius: 5px;
	border: 1px solid $bg-gray2;
	&:focus {
		@include trans(0.3);
		border: 1px solid $bg-green2;
		background-color: rgba(119, 194, 80, 0.2);
	}
	&.error {
		background-color: rgba(250, 183, 195, 0.3);
	}
	&-password {
		padding: 12px 25px 10px 6px;
	}
}
.input::placeholder {
	color: #d9d9d9;
}
input:disabled {
	color: $color-gray4;
	opacity: 1 !important;
	background: #eee;
}
.form_group {
	display: flex;
	align-items: center;
}
.form_group.error .input {
	color: $baseTextColor;
	border-color: $bg-red2;
	background-color: rgba(250, 183, 195, 0.2);
}
</style>
